import { useState } from 'react';
import axios from 'axios';
import { Download, Loader2 } from 'lucide-react';
import { API } from '../App';

/**
 * Reusable authenticated file-download button.
 *
 * Triggers an axios blob fetch with the standard Bearer token (via the
 * axios default auth header set by AuthContext), converts the response
 * to an object URL, and dispatches a synthetic <a download> click. This
 * is the same pattern that fixed the earlier 401 bug — do NOT revert to
 * plain `<a href>` navigation, which drops the Authorization header.
 *
 * Props:
 *   endpoint — path relative to {API}, e.g. "/exports/balances.csv"
 *   params   — optional query params dict (e.g. { year: 2026 })
 *   filename — suggested filename for the download
 *   label    — visible button copy (defaults to "Export CSV")
 *   testId   — data-testid to attach to the button
 *   disabled — forwarded disabled flag
 *   variant  — "csv" (green) | "xlsx" (green with .xlsx copy)
 */
function ExportButton({
  endpoint,
  params = {},
  filename,
  label = 'Export CSV',
  testId,
  disabled = false,
  className = '',
}) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    if (downloading || disabled) return;
    setDownloading(true);
    try {
      const res = await axios.get(`${API}${endpoint}`, {
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Unable to export right now. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={downloading || disabled}
      data-testid={testId}
      className={`flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors ${className}`}
    >
      {downloading ? (
        <Loader2 className="w-4 h-4 animate-spin" />
      ) : (
        <Download className="w-4 h-4" />
      )}
      <span>{downloading ? 'Exporting…' : label}</span>
    </button>
  );
}

export default ExportButton;
